import { useState, useCallback, useEffect, useMemo } from 'react'
import { useFlipFlowData } from './useFlipFlowData'
import { useShowMissingPhoneModal } from './useShowMissingPhoneModal'

/**
 * Options for configuring modal auto-open behavior
 * @interface FlipModalsOptions
 */
export interface FlipModalsOptions {
  /**
   * Whether to automatically open the EnrollOrReplace modal on mount
   * @default false
   */
  autoOpenEnrollOrReplace?: boolean

  /**
   * Whether to check replacement eligibility when determining auto-open
   * @default false
   */
  checkReplacementEligibility?: boolean
}

/**
 * State and control functions for managing Flip flow modals
 * @interface FlipModalsState
 */
export interface FlipModalsState {
  /** Visibility state for Learn More modal */
  showLearnMoreModal: boolean

  /** Visibility state for Enroll or Replace modal */
  showEnrollOrReplaceModal: boolean

  /** Visibility state for Expired Scope modal */
  showExpiredScopeModal: boolean

  /** Visibility state for Missing Phone modal (accounts for auto-hide) */
  showMissingPhoneModal: boolean

  /** Opens the Learn More modal */
  openLearnMoreModal: () => void

  /** Closes the Learn More modal */
  closeLearnMoreModal: () => void

  /** Opens the Enroll or Replace modal */
  openEnrollOrReplaceModal: () => void

  /** Closes the Enroll or Replace modal */
  closeEnrollOrReplaceModal: () => void

  /** Opens the Expired Scope modal */
  openExpiredScopeModal: () => void

  /** Closes the Expired Scope modal */
  closeExpiredScopeModal: () => void

  /** Hides the Missing Phone modal (persists for the session) */
  hideMissingPhoneModal: () => void

  /** Closes all modals at once */
  closeAllModals: () => void

  /** Indicates whether any modal is currently open */
  hasOpenModal: boolean
}

/**
 * Custom hook for managing modal states in Flip flow components.
 * Provides centralized modal state management with auto-open logic
 * based on replacement eligibility and feature flags.
 *
 * @param {FlipModalsOptions} options - Configuration options for modal behavior
 * @returns {FlipModalsState} Modal states and control functions
 *
 * @example
 * ```typescript
 * const {
 *   showLearnMoreModal,
 *   showEnrollOrReplaceModal,
 *   openLearnMoreModal,
 *   closeLearnMoreModal,
 *   closeAllModals,
 *   hasOpenModal
 * } = useFlipModals({
 *   autoOpenEnrollOrReplace: true,
 *   checkReplacementEligibility: true
 * })
 *
 * // Use in JSX
 * <LearnMoreModal
 *   show={showLearnMoreModal}
 *   onClose={closeLearnMoreModal}
 * />
 * ```
 */
export const useFlipModals = (
  options: FlipModalsOptions = {}
): FlipModalsState => {
  const {
    autoOpenEnrollOrReplace = false,
    checkReplacementEligibility = false
  } = options

  const flowData = useFlipFlowData()
  const {
    showMissingPhoneModal: rawShowMissingPhoneModal
  } = useShowMissingPhoneModal()

  // Modal visibility states
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false)
  const [showEnrollOrReplaceModal, setShowEnrollOrReplaceModal] = useState(
    false
  )
  const [showExpiredScopeModal, setShowExpiredScopeModal] = useState(false)
  const [hideAutoMissingInfoModal, setHideAutoMissingInfoModal] = useState(
    false
  )

  const showMissingPhoneModal =
    rawShowMissingPhoneModal && !hideAutoMissingInfoModal

  // Auto-open EnrollOrReplace modal based on replacement eligibility
  useEffect(() => {
    if (
      autoOpenEnrollOrReplace &&
      checkReplacementEligibility &&
      flowData.replacementEligibility?.eligible
    ) {
      setShowEnrollOrReplaceModal(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Learn More modal controls
  const openLearnMoreModal = useCallback(() => {
    setShowLearnMoreModal(true)
  }, [])

  const closeLearnMoreModal = useCallback(() => {
    setShowLearnMoreModal(false)
  }, [])

  // Enroll or Replace modal controls
  const openEnrollOrReplaceModal = useCallback(() => {
    setShowEnrollOrReplaceModal(true)
  }, [])

  const closeEnrollOrReplaceModal = useCallback(() => {
    setShowEnrollOrReplaceModal(false)
  }, [])

  // Expired Scope modal controls
  const openExpiredScopeModal = useCallback(() => {
    setShowExpiredScopeModal(true)
  }, [])

  const closeExpiredScopeModal = useCallback(() => {
    setShowExpiredScopeModal(false)
  }, [])

  // Missing Phone modal control
  const hideMissingPhoneModal = useCallback(() => {
    setHideAutoMissingInfoModal(true)
  }, [])

  // Utility: Close all modals at once
  const closeAllModals = useCallback(() => {
    setShowLearnMoreModal(false)
    setShowEnrollOrReplaceModal(false)
    setShowExpiredScopeModal(false)
  }, [])

  // Computed: Check if any modal is open
  const hasOpenModal = useMemo(
    () =>
      showLearnMoreModal || showEnrollOrReplaceModal || showExpiredScopeModal,
    [showLearnMoreModal, showEnrollOrReplaceModal, showExpiredScopeModal]
  )

  return {
    showLearnMoreModal,
    showEnrollOrReplaceModal,
    showExpiredScopeModal,
    showMissingPhoneModal,
    openLearnMoreModal,
    closeLearnMoreModal,
    openEnrollOrReplaceModal,
    closeEnrollOrReplaceModal,
    openExpiredScopeModal,
    closeExpiredScopeModal,
    hideMissingPhoneModal,
    closeAllModals,
    hasOpenModal
  }
}
